import { Link, useParams } from 'react-router-dom';
import { works } from '@/mocks/works';
import { formatCount, formatDate } from '@/utils/format';
import Breadcrumb from '@/components/feature/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import Reveal from '@/components/base/Reveal';
import JsonLd from '@/components/base/JsonLd';
import { useSeo, buildAbsoluteUrl } from '@/hooks/useSeo';

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((item) => item.id === id);

  const detailTitle = work ? `${work.title} - 黑料大赛参赛作品 | 黑料大赛` : '作品未找到 | 黑料大赛';
  const detailDesc = work
    ? work.summary
    : '该作品可能已被移除或链接有误，请返回作品合集查看其他内容。';

  useSeo({
    title: detailTitle,
    description: detailDesc,
    keywords: work ? `${work.title},${work.category},黑料大赛参赛作品,${work.tags.join(',')}` : '黑料大赛',
    path: `/works/${id}`,
    ogType: 'article',
    image: work?.cover,
  });

  if (!work) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这篇作品</h1>
        <p className="mt-2 font-body text-sm text-foreground-600">链接可能已经失效，回合集页看看别的吧。</p>
        <Link
          to="/works"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          返回作品合集
        </Link>
      </div>
    );
  }

  const ranking = [...works].sort((a, b) => a.rank - b.rank).slice(0, 6);
  const related = works
    .filter((item) => item.id !== work.id && item.category === work.category)
    .slice(0, 2);
  const fallback = works.filter((item) => item.id !== work.id).slice(0, 3);
  const relatedList = related.length >= 2 ? related : fallback.slice(0, 2);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: buildAbsoluteUrl('/') },
          { '@type': 'ListItem', position: 2, name: '作品合集', item: buildAbsoluteUrl('/works') },
          { '@type': 'ListItem', position: 3, name: work.title, item: buildAbsoluteUrl(`/works/${work.id}`) },
        ],
      },
      {
        '@type': 'Article',
        headline: work.title,
        description: work.summary,
        image: work.cover,
        datePublished: work.publishedAt,
        dateModified: work.publishedAt,
        articleSection: work.category,
        keywords: work.tags.join(','),
        inLanguage: 'zh-CN',
        author: { '@type': 'Person', name: work.contestant },
        publisher: { '@type': 'Organization', name: '黑料大赛' },
        mainEntityOfPage: buildAbsoluteUrl(`/works/${work.id}`),
      },
    ],
  };

  return (
    <>
      <JsonLd data={schema} />
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '作品合集', to: '/works' },
              { label: work.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2 font-label text-xs">
            <span className="rounded-full bg-primary-100 px-3 py-1 text-primary-700">{work.category}</span>
            {work.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {work.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-4 font-label text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {work.contestant}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {formatDate(work.publishedAt)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              {formatCount(work.views)} 次围观
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-thumb-up-line"></i>
              {formatCount(work.likes)} 赞
            </span>
          </div>

          <div className="mt-8 h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-96">
            <img
              src={work.cover}
              alt={`${work.title} - 黑料大赛参赛作品封面`}
              title={`${work.title} - 黑料大赛参赛作品`}
              className="h-full w-full object-top"
            />
          </div>

          <p className="mt-8 rounded-lg border-l-4 border-primary-300 bg-background-100 p-4 font-body text-sm leading-relaxed text-foreground-700 md:p-5">
            {work.summary}
          </p>

          <div className="mt-8 flex flex-col gap-5">
            {work.content.map((paragraph, index) => (
              <p key={index} className="font-body text-base leading-loose text-foreground-800">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
            <div className="flex items-center gap-3">
              <span className="font-label text-xs text-foreground-500">信息来源：</span>
              <span className="flex items-center gap-1.5 rounded-md bg-background-100 px-3 py-1.5 font-label text-xs text-foreground-700">
                <i className="ri-shield-check-line text-secondary-600"></i>
                公开信息整理 · 来源可查
              </span>
            </div>
            <Link
              to="/works"
              className="flex items-center gap-1.5 whitespace-nowrap font-label text-sm text-primary-600 transition-colors hover:text-primary-700"
            >
              <i className="ri-arrow-left-line"></i>
              返回作品合集
            </Link>
          </div>

          <section className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {relatedList.map((item) => (
                <Reveal key={item.id}>
                  <WorkCard work={item} />
                </Reveal>
              ))}
            </div>
          </section>
        </article>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-trophy-line text-accent-600"></i>
                热门作品榜
              </h2>
              <ol className="mt-4 flex flex-col gap-3">
                {ranking.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/works/${item.id}`}
                      className={`flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-background-50 ${
                        item.id === work.id ? 'bg-primary-50' : ''
                      }`}
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-background-200 font-heading text-xs font-bold text-foreground-700">
                        {item.rank}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 font-body text-sm font-medium text-foreground-900">
                          {item.title}
                        </span>
                        <span className="mt-1 block font-label text-xs text-foreground-500">
                          {item.contestant} · 热度 {item.heat}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">对这篇作品有补充？</h2>
              <p className="mt-2 font-body text-sm leading-relaxed text-foreground-700">
                如果你有可查证的补充信息，欢迎邮件告诉我们，编辑会核实后更新作品。
              </p>
              <a
                href="mailto:heiliaodasai@example.com"
                className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-line"></i>
                联系编辑
              </a>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}